/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Audit logs", description = "Hightouch offers audit logs so workspace admins can see what actions users took in-app and when.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Audit logs",
        description: "Hightouch offers audit logs so workspace admins can see what actions users took in-app and when.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "View audit logs",
          "depth": 0,
          "id": "view-audit-logs"
        }, {
          "value": "Filter audit logs",
          "depth": 0,
          "id": "filter-audit-logs"
        }, {
          "value": "Export audit logs",
          "depth": 0,
          "id": "export-audit-logs"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "Who can view audit logs?",
          "depth": 1,
          "id": "who-can-view-audit-logs"
        }, {
          "value": "What do audit logs include?",
          "depth": 1,
          "id": "what-do-audit-logs-include"
        }, {
          "value": "Resource types and tracked actions",
          "depth": 0,
          "id": "resource-types-and-tracked-actions"
        }, {
          "value": "Older resource types",
          "depth": 1,
          "id": "older-resource-types"
        }, {
          "value": "Changes not in the Resource type filter",
          "depth": 1,
          "id": "changes-not-in-the-resource-type-filter"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      h2: "h2",
      strong: "strong",
      img: "img",
      ul: "ul",
      li: "li",
      ol: "ol",
      h3: "h3",
      code: "code",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      em: "em"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Audit logs",
        plural: true
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch offers audit logs so workspace admins can see what actions users took in-app and when. For example, suppose someone notices that a sync has sent incorrect data to a destination. A workspace admin can use audit logs to review changes made to the sync before the incorrect data appeared. The admin can correct the issue, and—if necessary—implement ", _jsx(_components.a, {
          href: "/workspace-management/overview#roles",
          children: "controls"
        }), " to avoid future incidents."]
      }), "\n", _jsx(_components.h2, {
        id: "view-audit-logs",
        children: "View audit logs"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "#who-can-view-audit-logs",
          children: "Workspace admins"
        }), " can view logs by going to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/settings/audit-log?from=docs",
          children: [_jsx(_components.strong, {
            children: "Audit log"
          }), " tab"]
        }), " on the ", _jsx(_components.strong, {
          children: "Settings"
        }), " page. The logs consist of a table of actions beginning with the most recent. Each row represents one action."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/audit-logs/audit-log-table.png",
          alt: "Audit log table listing recent actions with their date, member, action, and resource type"
        })
      }), "\n", _jsx(_components.p, {
        children: "Each action includes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "Date"
          }), " and time, in the viewer's timezone, the action took place"]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "Action"
          }), " name, for example, \"Sync Updated\""]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "Member"
          }), " who performed the action, with an ", _jsx(_components.strong, {
            children: "API key"
          }), " badge if they made the change with an API key"]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "Resource type"
          }), " of the resource the action was performed on"]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "Resource name"
          }), " of the resource the action was performed on"]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Clicking on an action opens its ", _jsx(_components.strong, {
          children: "Action details"
        }), " panel, which shows the full change in JSON format. Added lines are highlighted in green, while removed lines are highlighted in red."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/audit-logs/audit-log-details.png",
          alt: "Audit log action details showing the JSON diff, with removed lines in red and added lines in green"
        })
      }), "\n", _jsx(_components.h2, {
        id: "filter-audit-logs",
        children: "Filter audit logs"
      }), "\n", _jsx(_components.p, {
        children: "You can filter logs by:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "the workspace member who performed the action"
        }), "\n", _jsx(_components.li, {
          children: "the API key used to perform the action"
        }), "\n", _jsx(_components.li, {
          children: "the resource type the action was performed on"
        }), "\n", _jsx(_components.li, {
          children: "the resource name the action was performed on"
        }), "\n", _jsx(_components.li, {
          children: "a range of dates"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can select multiple members, API keys, and resource types to filter by simultaneously."
      }), "\n", _jsx(_components.p, {
        children: "The table shows up to the first 1,000 matching entries. If you're looking for an older action, add filters to narrow the results."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/audit-logs/audit-log-filters.png",
          alt: "Audit log filters for workspace member, API key, resource type, resource name, and date range"
        })
      }), "\n", _jsx(_components.h2, {
        id: "export-audit-logs",
        children: "Export audit logs"
      }), "\n", _jsx(_components.p, {
        children: "Workspace admins can export the audit log as a CSV file, for example to keep a copy for compliance reviews. The export includes the workspace's full audit log, not only the entries that match your current filters."
      }), "\n", _jsx(_components.p, {
        children: "To export the audit log:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/settings/audit-log?from=docs",
            children: [_jsx(_components.strong, {
              children: "Audit log"
            }), " tab"]
          }), " on the ", _jsx(_components.strong, {
            children: "Settings"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Export"
          }), ", then confirm."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch emails you a link to download the CSV file when the export finishes, which can take several minutes. The link expires after seven days."
      }), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "who-can-view-audit-logs",
        children: "Who can view audit logs?"
      }), "\n", _jsxs(_components.p, {
        children: ["Users with the ", _jsxs(_components.a, {
          href: "/workspace-management/roles#pre-built-roles",
          children: [_jsx(_components.strong, {
            children: "Workspace admin"
          }), " role"]
        }), " in a workspace can view its full audit log. Organization admins receive this role automatically. ", _jsx(_components.a, {
          href: "/workspace-management/roles#custom-roles",
          children: "Custom roles"
        }), " can't grant access to the full audit log."]
      }), "\n", _jsxs(_components.p, {
        children: ["Users without that role can still see the change history of an individual sync, model, or audience on its ", _jsx(_components.strong, {
          children: "Activity"
        }), " tab, as long as they can view that resource."]
      }), "\n", _jsx(_components.h3, {
        id: "what-do-audit-logs-include",
        children: "What do audit logs include?"
      }), "\n", _jsx(_components.p, {
        children: "Audit logs track user-driven activity vs. programmatic changes. They work by taking a JSON snapshot of the changed resource for every user-driven change. Hightouch uses these snapshots for a before and after comparison for any changes."
      }), "\n", _jsxs(_components.p, {
        children: ["A snapshot may include programmatic fields such as ", _jsx(_components.code, {
          children: "last_run_at"
        }), ", but Hightouch doesn't append a new audit log every time a field like ", _jsx(_components.code, {
          children: "last_run_at"
        }), " is updated since sync runs can be configured to run at regular intervals."]
      }), "\n", _jsxs(_components.p, {
        children: ["The exception to this is activity driven by ", _jsx(_components.a, {
          href: "/extensions/git-sync",
          children: "Git Sync"
        }), ". For example, if a user changes a configuration via ", _jsx(_components.a, {
          href: "/extensions/git-sync",
          children: "Git Sync"
        }), ", Hightouch treats that as a user-driven change, and it appears in the audit logs."]
      }), "\n", _jsxs(_components.p, {
        children: ["For a complete list of tracked actions, see the following ", _jsx(_components.a, {
          href: "#resource-types-and-tracked-actions",
          children: "list"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "resource-types-and-tracked-actions",
        children: "Resource types and tracked actions"
      }), "\n", _jsx(_components.p, {
        children: "Audit logs show creation, update, and deletion type actions for the following resource types:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Resource type"
            }), _jsx(_components.th, {
              children: "Tracked actions"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Alert"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/syncs/alerting",
                children: "alerts"
              }), " you configure to inform you of syncing issues"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Analytics query"
              })
            }), _jsxs(_components.td, {
              children: ["Queries Hightouch runs against your source to calculate ", _jsx(_components.a, {
                href: "/campaign-intelligence/overview",
                children: "Intelligence"
              }), " charts and funnels, including the SQL it ran"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "API key"
              })
            }), _jsxs(_components.td, {
              children: ["Creations and deletions of ", _jsx(_components.a, {
                href: "/developer-tools/api-guide",
                children: "API keys"
              }), " ", _jsx("br", {}), _jsx(_components.em, {
                children: "Note: Audit logs record changes made with an API key, not every request that uses it"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Approval request"
              })
            }), _jsxs(_components.td, {
              children: ["Actions taken during ", _jsx(_components.a, {
                href: "/workspace-management/approval-flows",
                children: "approval flows"
              }), ", including sending a request to review a draft or approving a draft"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/customer-studio/overview",
                children: "audience"
              }), " configurations"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience schema"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/customer-studio/schema",
                children: "parent models, relationships, and event models"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience split"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of split groups in audience ", _jsx(_components.a, {
                href: "/customer-studio/experiments",
                children: "experiments"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience template"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/customer-studio/templates#audience-templates",
                children: "audience templates"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Cloud credential"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of cloud credentials, including changing your ", _jsx(_components.a, {
                href: "/security/storage",
                children: "external storage configuration"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Dashboard"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/campaign-intelligence/dashboards",
                children: "dashboards"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Dashboard chart"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/campaign-intelligence/charts",
                children: "charts"
              }), " on a dashboard"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Destination"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/destinations/overview",
                children: "destination"
              }), " configurations"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Destination rule"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/customer-studio/destination-rules",
                children: "destination rules"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Draft"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of drafts used in ", _jsx(_components.a, {
                href: "/workspace-management/approval-flows",
                children: "approval flows"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Event contract"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/events/contracts/management",
                children: "event contracts"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Event schema"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of the ", _jsx(_components.a, {
                href: "/events/contracts/management#1-define-the-event-schema",
                children: "event schemas"
              }), " in an event contract"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Git credential"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of Git credentials used for ", _jsx(_components.a, {
                href: "/extensions/git-sync",
                children: "Git Sync"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Git Sync configuration"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/extensions/git-sync",
                children: "Git Sync"
              }), " configurations; for example, changing your tracked Git branch"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "IDR"
              })
            }), _jsx(_components.td, {
              children: "Creations, updates, or deletions of identity graphs in Identity Resolution"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "IDR golden record"
              })
            }), _jsx(_components.td, {
              children: "Creations, updates, or deletions of an identity graph's Golden Record configuration"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "IDR model"
              })
            }), _jsx(_components.td, {
              children: "Creations, updates, or deletions of the models an identity graph uses as inputs"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Journey"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/customer-studio/journeys",
                children: "journeys"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Model"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/models/creating-models",
                children: "general model"
              }), " configurations, including updating the model query or primary key"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Model column"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/models/creating-models#column-descriptions",
                children: "model columns"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Model preview"
              })
            }), _jsxs(_components.td, {
              children: ["Actions taken while ", _jsx(_components.a, {
                href: "/models/creating-models#preview-model-results",
                children: "previewing a model"
              }), ", including a user clicking “Preview” while editing a model or running a SQL query against a source during model setup"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Model trait definition"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/customer-studio/traits",
                children: "traits"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Realtime audience sync"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of syncs for ", _jsx(_components.a, {
                href: "/real-time/overview",
                children: "realtime audiences"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Role"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/workspace-management/roles#custom-roles",
                children: "custom roles"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Session"
              })
            }), _jsx(_components.td, {
              children: "User sign-ins and sign-outs"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Source"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/sources/overview",
                children: "source"
              }), " configurations, including updating credentials or adding labels"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "SSO connection"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/workspace-management/sso",
                children: "SSO"
              }), " connections, including changing SAML settings"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subset"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/customer-studio/subsets",
                children: "subsets"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subset audience assignment"
              })
            }), _jsxs(_components.td, {
              children: ["Changes to which ", _jsx(_components.a, {
                href: "/customer-studio/subsets#how-subsets-apply-to-audiences",
                children: "subsets an audience is assigned to"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subset category"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/customer-studio/subsets#2-create-a-subset-category",
                children: "subset categories"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subset category required model"
              })
            }), _jsxs(_components.td, {
              children: ["Changes to whether a subset category is ", _jsx(_components.a, {
                href: "/customer-studio/subsets#3-decide-whether-the-category-is-required",
                children: "required"
              }), " for a parent model"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Subset grant"
              })
            }), _jsxs(_components.td, {
              children: ["Changes to which ", _jsx(_components.a, {
                href: "/customer-studio/subsets#5-grant-access-to-user-groups",
                children: "user groups can access a subset"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sync"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/syncs/overview",
                children: "sync"
              }), " configurations, including adding labels, scheduling, and turning a sync on or off"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sync run"
              })
            }), _jsxs(_components.td, {
              children: ["Actions taken while viewing a sync; for example, a user clicking “Run” to start a ", _jsx(_components.a, {
                href: "/syncs/schedule-sync-ui#manual-runs",
                children: "manual run"
              }), " ", _jsx("br", {}), _jsx(_components.em, {
                children: "Note: Audit logs don't include scheduled sync runs nor API-triggered sync runs"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sync template"
              })
            }), _jsxs(_components.td, {
              children: ["Creations, updates, or deletions of ", _jsx(_components.a, {
                href: "/customer-studio/templates#sync-templates",
                children: "sync templates"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "User group membership"
              })
            }), _jsxs(_components.td, {
              children: ["Additions and removals of users in ", _jsx(_components.a, {
                href: "/workspace-management/user-management#groups",
                children: "user groups"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Workspace"
              })
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/workspace-management/workspaces",
                children: "Workspace"
              }), " creation"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "older-resource-types",
        children: "Older resource types"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Resource type"
        }), " filter also lists three types that Hightouch no longer records. They hold entries from before Hightouch moved workspace access to user groups:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Invite"
          }), " — Workspace invitations. Invitations sent today are organization-level invitations, which workspace audit logs don't record."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Membership"
          }), " — Workspace memberships. Adding or removing someone from a group now appears as ", _jsx(_components.strong, {
            children: "User group membership"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Membership request"
          }), " — Requests to join a workspace. Hightouch no longer supports these requests."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "changes-not-in-the-resource-type-filter",
        children: "Changes not in the Resource type filter"
      }), "\n", _jsxs(_components.p, {
        children: ["Some changes appear in the audit log but aren't in the ", _jsx(_components.strong, {
          children: "Resource type"
        }), " filter, so you can't filter for them by type. These include changes to which user groups can access the workspace, the roles and grants those groups have in it, and Identity Resolution graph runs. To find them, filter by member, resource name, or date range instead."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
